import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HISTORY_IMPORT_SUPPORTED_PROVIDERS } from '@troia-v3/types';
import type { ChannelResponse } from '@/modules/channels/types';
import { StatusPill, type StatusPillVariant } from '@/shared/components/ui/StatusPill';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Icon, IntegrationIcon } from '@/shared/icons';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { WidgetSettingsModal } from './WidgetSettingsModal';
import { IntegrationCredentialsModal } from './IntegrationCredentialsModal';
import { ImportHistoryModal } from './import-history/ImportHistoryModal';
import { formatChannelIdentifier } from '../utils/channelTypeMetadata';
import { isEmailChannel } from '../utils/isEmailChannel';
import { resolveDisconnectReason } from '../constants/disconnectReasons';

export interface ChannelListRowProps {
  channel: ChannelResponse;
  members?: Array<{ id: string; name: string; avatar?: string }>;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onReconnect?: (id: string) => void;
}

/** Rótulo curto do tipo de canal por providerId (usado na coluna "Tipo" e no filtro). */
export function channelTypeLabel(providerId: string | undefined): string {
  switch (providerId) {
    case 'gateway-whatsapp':
    case 'whatsapp-business':
    case 'meta':
      return 'WhatsApp';
    case 'instagram-messaging':
      return 'Instagram';
    case 'facebook-messenger':
      return 'Messenger';
    case 'telegram-bot':
      return 'Telegram';
    case 'website-widget':
    case 'website-chat':
      return 'Widget';
    case 'email-smtp':
    case 'email-sendgrid':
    case 'email-ses':
    case 'email-resend':
    case 'gmail-api':
      return 'Email';
    case 'api-webhook':
      return 'API';
    default:
      return 'Outro';
  }
}

function getInitials(displayName: string | undefined, fallbackName: string | undefined): string {
  const source = displayName?.trim() || fallbackName?.trim() || '';
  if (!source) return '?';
  const parts = source.split(/\s+/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0] ?? '').slice(0, 2).toUpperCase() || '?';
}

/**
 * Linha de tabela do canal (view "Lista"). Cobre canais de mensagem e contas de
 * email num único table flat. Preserva as mesmas ações do ChannelCard /
 * EmailAccountListItem (Configurações, Dashboard, Incorporação, Integração,
 * Importar histórico, Reconectar, Excluir) e seus modais.
 */
export const ChannelListRow: React.FC<ChannelListRowProps> = ({
  channel,
  members = [],
  selected = false,
  onToggleSelect,
  onDelete,
  onReconnect,
}) => {
  const navigate = useNavigate();
  const [showWidgetModal, setShowWidgetModal] = React.useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = React.useState(false);
  const [showImportModal, setShowImportModal] = React.useState(false);

  const email = isEmailChannel(channel);

  // StatusPill (F62): verde=ligado, âmbar=aguardando, neutro=inativo
  const statusVariant: StatusPillVariant =
    channel.status === 'active' ? 'success' : channel.status === 'pending' ? 'warning' : 'neutral';
  const statusLabel =
    channel.status === 'active' ? 'Ativo' : channel.status === 'pending' ? 'Aguardando' : 'Inativo';

  const accountInfo = channel.accountInfo;
  const pushName = accountInfo?.businessName || accountInfo?.verifiedName || accountInfo?.pushName;
  const formattedIdentifier = formatChannelIdentifier(channel.providerId, channel.identifier);
  const operatorsCount = members.length;

  // Ações — espelham ChannelCard (mensagem) / EmailAccountListItem (email).
  const actions: CardAction[] = [
    { label: 'Configurações', icon: 'settings', onClick: () => navigate(`/settings/channels/${channel.id}/edit`) },
  ];
  if (!email) {
    actions.push({ label: 'Dashboard', icon: 'bar-chart', onClick: () => navigate(`/settings/channels/${channel.id}/dashboard`) });
    if (channel.providerId === 'website-widget') {
      actions.push({ label: 'Incorporação', icon: 'code', onClick: () => setShowWidgetModal(true) });
    }
  }
  if (channel.integration?.instanceKey) {
    actions.push({ label: 'Integração', icon: 'key', onClick: () => setShowIntegrationModal(true) });
  }
  if (!email && channel.providerId && HISTORY_IMPORT_SUPPORTED_PROVIDERS.includes(channel.providerId) && channel.status === 'active') {
    actions.push({ label: 'Importar histórico', icon: 'download', onClick: () => setShowImportModal(true) });
  }
  if (onReconnect) {
    actions.push({ label: 'Reconectar', icon: 'refresh', onClick: () => onReconnect(channel.id) });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(channel.id), variant: 'danger' });
  }

  const disconnectInfo = channel.disconnectInfo;
  const showTooltip =
    (channel.status === 'error' || channel.status === 'suspended' || channel.status === 'pending') &&
    !!disconnectInfo?.reason;
  const friendly = disconnectInfo ? resolveDisconnectReason(disconnectInfo) : '';
  const dotColor =
    channel.status === 'active'
      ? '#22c55e'
      : channel.status === 'pending'
        ? '#f59e0b'
        : channel.status === 'error' || channel.status === 'suspended'
          ? '#ef4444'
          : '#cbd5e1';
  const dot = (
    <span
      className={`inline-block flex-shrink-0 rounded-full${showTooltip ? ' cursor-help' : ''}`}
      style={{ width: 8, height: 8, backgroundColor: dotColor }}
      aria-label={statusLabel}
      data-testid={`channel-row-status-dot-${channel.id}`}
    />
  );

  return (
    <>
      {channel.providerId === 'website-widget' && (
        <WidgetSettingsModal isOpen={showWidgetModal} onClose={() => setShowWidgetModal(false)} channel={channel} />
      )}
      <IntegrationCredentialsModal
        isOpen={showIntegrationModal}
        onClose={() => setShowIntegrationModal(false)}
        instanceKey={channel.integration?.instanceKey || null}
        instanceToken={channel.integration?.instanceToken || null}
        channelName={channel.name}
      />
      <ImportHistoryModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        channelId={channel.id}
      />

      <tr
        className="border-b transition-colors"
        style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
        onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
        data-testid={`channel-row-${channel.id}`}
      >
        {/* Checkbox */}
        <td className="pl-4 pr-1 py-3 w-11">
          <button
            type="button"
            onClick={() => onToggleSelect?.(channel.id)}
            className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
            style={{
              backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
              borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
              color: '#ffffff',
            }}
            aria-label={selected ? 'Desmarcar' : 'Selecionar'}
            data-testid={`channel-row-check-${channel.id}`}
          >
            {selected && <Icon name="check" className="w-3 h-3" />}
          </button>
        </td>

        {/* Canal: avatar + nome + identificador */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex-shrink-0">
              {/* Squircle 36 (F44) */}
              <Avatar className="h-9 w-9" isSquircle>
                {accountInfo?.profilePictureUrl && (
                  <AvatarImage src={accountInfo.profilePictureUrl} alt={channel.name} isSquircle />
                )}
                <AvatarFallback
                  isSquircle
                  style={{ background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#ffffff', fontSize: 12, fontWeight: 600 }}
                >
                  {getInitials(pushName, channel.name)}
                </AvatarFallback>
              </Avatar>
              <div
                className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full"
                style={{ width: 16, height: 16, backgroundColor: '#ffffff', border: '2px solid #ffffff', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' }}
                aria-hidden
              >
                <IntegrationIcon id={channel.providerId || 'unknown'} size={10} variant="brand" />
              </div>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span
                  className="font-semibold text-sm truncate"
                  style={{ color: 'var(--theme-card-text)' }}
                  data-testid={`channel-row-name-${channel.id}`}
                >
                  {channel.name}
                </span>
                {showTooltip ? (
                  <Tooltip
                    side="top"
                    content={
                      <div className="max-w-[240px]">
                        <div className="font-semibold mb-0.5">Desconectado</div>
                        <div className="font-normal opacity-90">{friendly}</div>
                      </div>
                    }
                  >
                    {dot}
                  </Tooltip>
                ) : dot}
                {channel.integration?.accountMode === 'COEXISTENCE' && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0"
                    style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)', color: 'var(--theme-primary)', border: '1px solid rgba(139, 92, 246, 0.3)' }}
                    title="Número em modo Coexistência"
                  >
                    Coexistência
                  </span>
                )}
              </div>
              {(formattedIdentifier || channel.identifier) && (
                <div
                  className="text-xs truncate"
                  style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {formattedIdentifier || channel.identifier}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Tipo */}
        <td className="px-4 py-3">
          <span
            className="inline-flex items-center h-[23px] px-2.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
          >
            {channelTypeLabel(channel.providerId)}
          </span>
        </td>

        {/* Operadores */}
        <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}>
          {operatorsCount > 0 ? operatorsCount : <span style={{ color: 'var(--theme-muted-text)' }}>—</span>}
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          <StatusPill variant={statusVariant} label={statusLabel} data-testid={`channel-row-status-${channel.id}`} />
        </td>

        {/* Ações */}
        <td className="px-4 py-3 text-right w-14">
          <CardActionsMenu actions={actions} size="sm" data-testid={`channel-row-actions-${channel.id}`} />
        </td>
      </tr>
    </>
  );
};

ChannelListRow.displayName = 'ChannelListRow';
